import { lazy, Suspense, useEffect } from "react";
import AppLayout from "@/layout";
import { Route, Routes } from "react-router-dom";
import { ROUTES } from "@/lib/routes";

import PublicRoute from "./public";
import PrivateRoute from "./private";

import { useUserDetails } from "@/redux/function";
import { bootstrapPreview } from "@/helper/bootstrap";

import { Loader, PageNotFound, RouteErrorBoundary } from "@workspace/shared";

import { ModuleName } from "@/lib/moduleNamesEnum";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Login = lazy(() => import("@/pages/auth/login"));

const ForgotPassword = lazy(() =>
  import("@/pages/auth/forgotPassword").then((m) => ({
    default: m.ForgotPassword,
  }))
);

const OtpVerification = lazy(() =>
  import("@/pages/auth/otpVerification").then((m) => ({
    default: m.OtpVerification,
  }))
);

const CheckEmailInstruction = lazy(() =>
  import("@/pages/auth/CheckEmailInstruction").then((m) => ({
    default: m.CheckEmailInstruction,
  }))
);

const ResetPassword = lazy(() =>
  import("@/pages/auth/ResetPassword").then((m) => ({
    default: m.ResetPassword,
  }))
);

const CommissionRules = lazy(() => import("@/pages/commissionRules"));
const AddEditCommissionRules = lazy(
  () => import("@/pages/commissionRules/form")
);

const RunPayout = lazy(() =>
  import("@/pages/runPayout").then((m) => ({
    default: m.RunPayout,
  }))
);

const PayoutLog = lazy(() =>
  import("@/pages/payoutLog").then((m) => ({
    default: m.PayoutLog,
  }))
);

const Target = lazy(() => import("@/pages/target"));
const AddEditTarget = lazy(() => import("@/pages/target/form"));

const SettingPage = lazy(() => import("@/pages/settings"));

const Partner = lazy(() =>
  import("@/pages/partner").then((m) => ({
    default: m.Partner,
  }))
);

const AddEditPartner = lazy(() =>
  import("@/pages/partner/form").then((m) => ({
    default: m.AddEditPartner,
  }))
);

const ViewPartner = lazy(() =>
  import("@/pages/partner/view").then((m) => ({
    default: m.ViewPartner,
  }))
);

const ViewMappedUser = lazy(() =>
  import("@/pages/partner/components/mappedUser/form").then((m) => ({
    default: m.ViewMappedUser,
  }))
);

const AdminAccount = lazy(() => import("@/pages/account"));

const MessageCreditConfiguration = lazy(() =>
  import("@/pages/messageCreditConfiguration").then((m) => ({
    default: m.MessageCreditConfiguration,
  }))
);

const TenantSubscription = lazy(() =>
  import("@/pages/tenantSubscription").then((m) => ({
    default: m.TenantSubscription,
  }))
);

const Coupon = lazy(() =>
  import("@/pages/coupon").then((m) => ({
    default: m.Coupon,
  }))
);

const AddEditCoupon = lazy(() => import("@/pages/coupon/form"));

const ModelWiseConfiguration = lazy(() =>
  import("@/pages/modelWiseConfiguration").then((m) => ({
    default: m.ModelWiseConfiguration,
  }))
);

const ModelWiseConfigForm = lazy(() =>
  import("@/pages/modelWiseConfiguration/form").then((m) => ({
    default: m.ModelWiseConfigForm,
  }))
);

const AppRoutes = () => {
  const userData = useUserDetails();
  const accessToken = userData?.access;
  useEffect(() => {
    if (accessToken) {
      bootstrapPreview();
    }
  }, [accessToken]);
  return (
    <Suspense fallback={<Loader type="fullscreen" />}>
      <Routes>
        <Route element={<PublicRoute />}>
          <Route path={ROUTES.LOGIN.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Login}><Login /></RouteErrorBoundary>} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<RouteErrorBoundary moduleName={ModuleName.ForgotPassword}><ForgotPassword /></RouteErrorBoundary>} />
          <Route
            path={ROUTES.FORGOT_PASSWORD_OTP_VERIFICATION.BASE}
            element={<RouteErrorBoundary moduleName={ModuleName.OtpVerification}><OtpVerification /></RouteErrorBoundary>}
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD_OTP_VERIFICATION.SENT_EMAIL}
            element={<RouteErrorBoundary moduleName={ModuleName.CheckEmailInstruction}><CheckEmailInstruction /></RouteErrorBoundary>}
          />
          <Route
            path={ROUTES.CHANGE_PASSWORD.TOKEN(":token")}
            element={<RouteErrorBoundary moduleName={ModuleName.ResetPassword}><ResetPassword /></RouteErrorBoundary>}
          />
        </Route>
        <Route element={<PrivateRoute />}>
          <Route path="*" element={<PageNotFound />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<RouteErrorBoundary moduleName={ModuleName.Dashboard}><Dashboard /></RouteErrorBoundary>} />
            <Route
              path={ROUTES.PROFILE.BASE}
              element={<RouteErrorBoundary moduleName={ModuleName.AdminAccount}><AdminAccount /></RouteErrorBoundary>}
            />
            <Route
              path={ROUTES.COMMISSION_RULES.BASE}
              element={<RouteErrorBoundary moduleName={ModuleName.CommissionRules}><CommissionRules /></RouteErrorBoundary>}
            />
            <Route
              path={ROUTES.COMMISSION_RULES.CREATE}
              element={<RouteErrorBoundary moduleName={ModuleName.AddEditCommissionRules}><AddEditCommissionRules /></RouteErrorBoundary>}
            />
            <Route
              path={`${ROUTES.COMMISSION_RULES.BASE}/:id`}
              element={<RouteErrorBoundary moduleName={ModuleName.AddEditCommissionRules}><AddEditCommissionRules /></RouteErrorBoundary>}
            />
            <Route
              path={ROUTES.TARGET.BASE}
              element={<RouteErrorBoundary moduleName={ModuleName.Target}><Target /></RouteErrorBoundary>}
            />
            <Route
              path={ROUTES.TARGET.CREATE}
              element={<RouteErrorBoundary moduleName={ModuleName.AddEditTarget}><AddEditTarget /></RouteErrorBoundary>}
            />
            <Route
              path={`${ROUTES.TARGET.BASE}/:id`}
              element={<RouteErrorBoundary moduleName={ModuleName.AddEditTarget}><AddEditTarget /></RouteErrorBoundary>}
            />
            <Route path={ROUTES.PARTNER.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Partner}><Partner /></RouteErrorBoundary>} />
            <Route path={ROUTES.PARTNER.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditPartner}><AddEditPartner /></RouteErrorBoundary>} />
            <Route path={ROUTES.PARTNER.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditPartner}><AddEditPartner /></RouteErrorBoundary>} />
            <Route path={ROUTES.PARTNER.ID(":id").VIEW} element={<RouteErrorBoundary moduleName={ModuleName.ViewPartner}><ViewPartner /></RouteErrorBoundary>} />
            <Route
              path={ROUTES.PARTNER.VIEW_USER_BY_ID(":partnerId", ":id").BASE}
              element={<RouteErrorBoundary moduleName={ModuleName.ViewMappedUser}><ViewMappedUser /></RouteErrorBoundary>}
            />
            <Route path={ROUTES.RUN_PAYOUT.BASE} element={<RouteErrorBoundary moduleName={ModuleName.RunPayout}><RunPayout /></RouteErrorBoundary>} />
            <Route path={ROUTES.PAYOUT_LOG.BASE} element={<RouteErrorBoundary moduleName={ModuleName.PayoutLog}><PayoutLog /></RouteErrorBoundary>} />
            <Route path={ROUTES.SETTINGS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.SettingPage}><SettingPage /></RouteErrorBoundary>} />
            <Route path={ROUTES.MESSAGE_CREDIT_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.MessageCreditConfiguration}><MessageCreditConfiguration /></RouteErrorBoundary>} />

            <Route path={ROUTES.TENANT_SUBSCRIPTIONS.BASE} element={<RouteErrorBoundary moduleName={ModuleName.TenantSubscription}><TenantSubscription /></RouteErrorBoundary>} />
            <Route path={ROUTES.COUPON.BASE} element={<RouteErrorBoundary moduleName={ModuleName.Coupon}><Coupon /></RouteErrorBoundary>} />
            <Route path={ROUTES.COUPON.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCoupon}><AddEditCoupon /></RouteErrorBoundary>} />
            <Route path={`${ROUTES.COUPON.BASE}/:id`} element={<RouteErrorBoundary moduleName={ModuleName.AddEditCoupon}><AddEditCoupon /></RouteErrorBoundary>} />
            <Route path={ROUTES.MODEL_WISE_CONFIGURATION.BASE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfiguration}><ModelWiseConfiguration /></RouteErrorBoundary>} />
            <Route path={ROUTES.MODEL_WISE_CONFIGURATION.CREATE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfigForm}><ModelWiseConfigForm /></RouteErrorBoundary>} />
            <Route path={ROUTES.MODEL_WISE_CONFIGURATION.ID(":id").BASE} element={<RouteErrorBoundary moduleName={ModuleName.ModelWiseConfigForm}><ModelWiseConfigForm /></RouteErrorBoundary>} />
          </Route >
        </Route >
      </Routes >
    </Suspense>
  )
};

export default AppRoutes;